import React, { useState, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { hasPrivilege } from '../utils/hasPrivilege';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';

const Category = () => {
  const { userData } = useUser();
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState([]);
  const location = useLocation();
  const token = localStorage.getItem('auth-token');
  const { categoryAttributeId } = location.state || {};
  const urlParams = new URLSearchParams(location.search);
  const sportId = urlParams.get('sportId');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => {
    setLoading(true);
    setError(null);

    fetch(`${process.env.REACT_APP_API_CATEGORY_VIEW}?sportId=${sportId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch categories");
        }
        return response.json();
      })
      .then((data) => {
        if (data.success && data.data?.categories) {
          const formattedCategories = data.data.categories.map((category) => ({
            id: category.id,
            name: category.name,
          }));
          setCategories(formattedCategories);
        } else {
          throw new Error("Invalid data format");
        }
        setLoading(false);
      })
      .catch((error) => {
        setError(error.message);
        setLoading(false);
      });
  }, []);


  const navigate = useNavigate();

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleNewCategory = (categoryId) => {
    navigate('/createCategory', { state: { categoryId, sportId } });
  };


  const handleEdit = (categoryId, categoryName) => {
    navigate('/createCategory', {
      state: { categoryAttributeId, sportId, categoryId, categoryName }
    });
  };

  const handleDrill = (categoryId) => {
    navigate('/drills', { state: { sportId, categoryId } });
  };


  const handleDeleteCategory = async (categoryId) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Are you sure?',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    });

    if (!result.isConfirmed) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_CATEGORY_DELETE}?categoryId=${categoryId}`, {
        method: 'DELETE',
        headers: {
          'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();
      if (response.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Category deleted successfully!',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });

        setCategories(categories.filter(category => category.id !== categoryId));
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: data.message || 'Failed to delete category.',
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Something went wrong while deleting the category.',
      });
    }
  };

  const filteredCategories = categories.filter((category) =>
    Object.values(category)
      .join(' ')
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredCategories.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredCategories.length / rowsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prevPage) => prevPage - 1);
  };

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;

    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () => {
    return generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">
          {page}
        </span>
      )
    );
  };

  const totalEntries = filteredCategories.length;
  const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;

  return (
    <div className="enrollment-container">
      <div className="breadcrumb">
        <Link to="/sportList" className="breadcrumb-link">Sport List</Link> &gt; Category
      </div>

      <div className="search-bar">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? "has-value" : ""}`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <label htmlFor="searchInput" className="floating-label">
            🔍 Search
          </label>
        </div>
        <button
          className="create-button-new"
          onClick={() => handleNewCategory()}
        >
          <span className="create-button__icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="23"
              height="23"
              className="create-button__svg"
            >
              <path
                d="M12 5v14M5 12h14"
                stroke="white"
                strokeWidth="2.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="create-button__label">
            Create Category
          </span>
        </button>
      </div>

      {loading && (
        <div className="premium-loader">
          <div className="progress-track">
            <div className="progress-bar"></div>
          </div>
          <div className="status-text">
            <span>L</span>
            <span>O</span>
            <span>A</span>
            <span>D</span>
            <span>I</span>
            <span>N</span>
            <span>G</span>
          </div>
        </div>
      )}
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && filteredCategories.length === 0 && (
        <div className="error-message">No data available</div>
      )}

      <div className="tt-table-container">
        <table className="tt-table">
          <thead>
            <tr>
              <th>Category Name</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {currentRows.map((category) => (
              <tr key={category.id}>
                <td>{category.name}</td>
                <td className="action-buttons">
                  <button className="edit-btn tooltip-btn" onClick={() => handleEdit(category.id, category.name)}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="white"
                      strokeWidth="2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M16.862 3.487a2.12 2.12 0 0 1 3.001 3.001L7.5 18.85l-4 1 1-4L16.862 3.487z"
                      />
                    </svg>
                    <span className="tooltip-text">Edit</span>
                  </button>
                  <button className="delete-btn tooltip-btn" onClick={() => handleDeleteCategory(category.id, sportId, categoryAttributeId)}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="white"
                      strokeWidth="2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7z"
                      />
                    </svg>
                    <span className="tooltip-text">Delete</span>
                  </button>
                  <button className="drill-btn tooltip-btn" onClick={() => category?.id && handleDrill(category.id)}>
                    <svg
                      fill="white"
                      width="18"
                      height="18"
                      viewBox="0 0 511.998 511.998"
                      xmlns="http://www.w3.org/2000/svg"
                      stroke="white"
                    >
                      <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                      <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                      <g id="SVGRepo_iconCarrier">
                        <g>
                          <circle cx="264.358" cy="55.54" r="42.502"></circle>
                        </g>
                        <g>
                          <path d="M418.946,203.093c-9.346-5.34-21.253-2.092-26.593,7.255c-3.145,5.504-3.291,11.889-0.987,17.305l-3.069,5.372 
        l-29.226-30.973l-24.156-74.089c-2.414-7.404-8.961-12.298-16.251-12.975v-0.267h-94.239c-0.222-0.096-0.431-0.208-0.659-0.297 
        L166.9,92.138l-4.832-55.134l10.35,0.042c3.541,4.701,9.143,7.764,15.484,7.791c10.765,0.043,19.527-8.647,19.57-19.411 
        c0.043-10.765-8.647-19.527-19.412-19.57c-6.339-0.026-11.968,2.992-15.546,7.664l-12.904-0.052 
        c-2.524-8.414-10.677-14.185-19.774-13.396c-7.886,0.692-14.208,6.129-16.418,13.25l-5.138-0.021 
        c-3.541-4.701-9.143-7.765-15.484-7.791c-10.763-0.045-19.526,8.646-19.57,19.41c-0.044,10.765,8.647,19.527,19.412,19.57 
        c6.339,0.025,11.968-2.992,15.546-7.664l5.879,0.023l6.159,70.278c0.628,7.159,5.256,13.345,11.947,15.966l67.787,26.565 
        c0.032,0.013,0.065,0.021,0.099,0.033v132.598l-46.906,199.465c-3.11,13.228,5.091,26.474,18.319,29.585 
        c13.235,3.11,26.475-5.095,29.585-18.319l47.575-202.305h11.464l47.575,202.305c3.11,13.226,16.352,21.429,29.585,18.319 
        c13.229-3.111,21.43-16.357,18.319-29.585L318.664,282.29v-82.082l5.74,17.608c0.866,2.657,2.31,5.089,4.228,7.121l40.04,42.435 
        l-4.21,7.369c-5.836,0.765-11.261,4.133-14.406,9.638c-5.34,9.346-2.092,21.253,7.255,26.593 
        c9.346,5.34,21.253,2.092,26.593-7.255c3.145-5.504,3.291-11.889,0.987-17.305l2.415-4.227c6.139,1.285,12.386-0.51,16.88-4.749 
        c7.523-7.1,7.929-18.9,0.98-26.51l6.629-11.603c5.836-0.765,11.261-4.134,14.406-9.638 
        C431.541,220.339,428.292,208.434,418.946,203.093z"></path>
                        </g>
                      </g>
                    </svg>
                    <span className="tooltip-text">Drills</span>
                  </button>

                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button className="prev-button" onClick={handlePrevPage} disabled={currentPage === 1}>Previous</button>
        {renderPagination()}
        <button className="next-button" onClick={handleNextPage} disabled={currentPage === totalPages}>Next</button>
      </div>
    </div>
  );
};

export default Category;
